// composables/useToast.js
import { ref, h, render, nextTick } from "vue";
import Toast from "@/components/pebble/toast/index.vue";
import type {
  ToastType,
  ToastOptions,
  ToastProps,
  CustomToastPosition,
} from "#shared/types";
import { isClient } from "@vueuse/core";

const toasts = ref<ToastType[]>([]);
let toastId = 0;
const DURATION = 8000;
const TOAST_GAP = 12;
const TOP_MARGIN = 20;
const RIGHT_MARGIN = 20;

export function useToast() {
  const createToast = async (props: ToastProps) => {
    if (!isClient) return;

    const id = toastId++;
    const container = document.createElement("div");
    document.body.appendChild(container);

    const removeToast = () => {
      render(null, container);
      document.body.removeChild(container);

      // Remove from toasts array
      const index = toasts.value.findIndex(
        (toast: ToastType) => toast.id === id
      );
      if (index !== -1) {
        toasts.value.splice(index, 1);
      }

      updateToastPositions();
    };

    const index = toasts.value.length;

    const vnode = h(Toast as any, {
      ...props,
      id,
      index,
      onClose: removeToast,
    });

    render(vnode, container);

    const toast = {
      id,
      index,
      vnode,
      container,
      close: removeToast,
    };

    toasts.value.push(toast);

    await nextTick();
    updateToastPositions();

    return toast;
  };

  const updateToastPositions = () => {
    toasts.value.forEach((toast, index) => {
      toast.index = index;
      const updatedVnode = h(Toast as any, {
        ...toast.vnode.props,
        index,
        position: calculatePosition(index, toast.vnode.props?.position),
        onClose: toast.close,
      });
      render(updatedVnode, toast.container);
    });
  };

  const calculatePosition = (index: number, customPosition?: unknown) => {
    // Use custom position if provided, otherwise use defaults
    const baseTop = (customPosition as CustomToastPosition)?.top ?? TOP_MARGIN;
    const baseRight =
      (customPosition as CustomToastPosition)?.right ?? RIGHT_MARGIN;

    const verticalOffset = index * (70 + TOAST_GAP);

    return {
      top: baseTop + verticalOffset,
      right: baseRight,
    };
  };

  return {
    success: (title: string, message = "", options: ToastOptions = {}) =>
      createToast({
        type: "success",
        title,
        message,
        duration: options.duration || DURATION,
        position: options.position || { top: TOP_MARGIN, right: RIGHT_MARGIN },
        bordered: options.bordered || false,
      }),

    error: (title: string, message = "", options: ToastOptions = {}) =>
      createToast({
        type: "error",
        title,
        message,
        duration: options.duration || DURATION,
        position: options.position || { top: TOP_MARGIN, right: RIGHT_MARGIN },
        bordered: options.bordered || false,
      }),

    warning: (title: string, message = "", options: ToastOptions = {}) =>
      createToast({
        type: "warning",
        title,
        message,
        duration: options.duration || DURATION,
        position: options.position || { top: TOP_MARGIN, right: RIGHT_MARGIN },
        bordered: options.bordered || false,
      }),

    info: (title: string, message = "", options: ToastOptions = {}) =>
      createToast({
        type: "info",
        title,
        message,
        duration: options.duration || DURATION,
        position: options.position || { top: TOP_MARGIN, right: RIGHT_MARGIN },
        bordered: options.bordered || false,
      }),

    custom: (props: ToastProps) => createToast(props),

    closeAll: () => {
      toasts.value.forEach((toast) => toast.close());
    },

    getToastsCount: () => toasts.value.length,

    getToasts: () => toasts.value,
  };
}
